import { useParams } from 'react-router-dom'

import { Link, Text } from '@factorialco/deprecated-design-system'
import { ProjectionKind, useMySessionsDataTable } from 'generated/resources/datatable.graphql'

import DataTable from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { StatusTag } from 'modules/trainings/components/Revamp/MyTrainings/SessionDetail/StatusTag'
import { formatSessionDate } from 'modules/trainings/lib/utils'

export function MySessionsDatatable({
  action,
}: {
  action({ id }: { id: string }): React.ReactNode
}) {
  const { trainingId } = useParams<{ trainingId: string }>()
  const { debouncedSearch, search, onSearch } = useDebouncedSearch()
  const { access } = useCurrentSession()
  const variables = { search: debouncedSearch, trainingId: String(trainingId), accessId: access.id }

  const source = useMySessionsDataTable(
    [
      {
        identifier: 'name',
        kind: ProjectionKind.Path,
        title: i18n.t('trainings.my_trainings_revamp.sessions_datatable.name'),
        path: 'name',
      },
      {
        identifier: 'date',
        width: 300,
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.my_trainings_revamp.sessions_datatable.date'),
        render: ({ startsAt, endsAt, duration, schedule }) =>
          formatSessionDate({
            startsAt,
            endsAt,
            duration,
            schedule,
          }),
        template: '{{ date }}',
      },
      {
        identifier: 'location',
        kind: ProjectionKind.Path,
        title: i18n.t('trainings.my_trainings_revamp.sessions_datatable.location'),
        path: 'location',
      },
      {
        identifier: 'link',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.my_trainings_revamp.sessions_datatable.link'),
        render: ({ link }) =>
          link ? (
            <Link.Href href={link} external title="Link">
              <Text color="textLink" size="300">
                {'Link'}
              </Text>
            </Link.Href>
          ) : null,
        template: '{{link}}',
      },
      {
        identifier: 'status',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.my_trainings_revamp.sessions_datatable.status'),
        render: ({ sessionAttendances }) => {
          const sessionAttendance = sessionAttendances.find(
            (attendance) => attendance.accessId === access.id
          )
          const canEdit = sessionAttendance?._policies?.edit.can ?? false
          return sessionAttendance ? (
            <StatusTag canEditAttendance={canEdit} sessionAttendance={sessionAttendance} />
          ) : null
        },
        template: '{{status}}',
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 80,
        render: action,
      },
    ],
    {
      variables: variables,
    }
  )
  return (
    <DataTable
      source={source}
      disableDefaultActions
      search={{
        placeholder: i18n.t('common.search'),
        value: search,
        onChange: onSearch,
      }}
    />
  )
}
